import { useState } from 'react';
import {
  Autocomplete,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  createFilterOptions,
  Popper,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useApolloClient } from '@apollo/client';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import getConfig from 'next/config';
import {
  validateEircode,
  getAddresses,
  formatAddress,
  createAddressObject,
} from './Broadband.util';
import styles from './styles';

const { publicRuntimeConfig } = getConfig();

const CustomPopper = props => {
  const theme = useTheme();
  return <Popper {...props} sx={styles(theme).AutocompleteListItems} placement='bottom' />;
};

/**
 * @name BroadbandPrequalInput - Eircode inpput field with Check availability button
 */
const BroadbandPrequalInput = props => {
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const graphql = useApolloClient();
  const [isEirCodeValid, setIsEirCodeValid] = useState(true);
  const [disableButton, setDisableButton] = useState(true);
  const [addresses, setAddresses] = useState([]);
  const [eirCode, setEirCode] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState({});

  const filterOptions = createFilterOptions({
    stringify: option => option.userEnteredEirCode,
  });
  /**
   * @name setAddressList - Is called when correct eircode provided on prequal form
   * @param {string} validEircode - Eircode provided on the prequal form
   */
  const setAddressList = async eircode => {
    let validEircode;
    if (eircode && eircode.length >= 7) {
      validEircode = validateEircode(eircode);
      if (validEircode.length > 0) {
        setEirCode(validEircode);
        setIsEirCodeValid(true);
        try {
          const data = await getAddresses(graphql, validEircode);
          const fullList = data.map(val => ({
            ...val,
            userEnteredEirCode: eircode,
          }));
          setAddresses(fullList);
          setOpen(true);
        } catch (err) {
          setAddresses([]);
          setDisableButton(true);
        }
      } else if (eircode.length >= 7 && validEircode.length === 0) {
        setIsEirCodeValid(false);
        setAddresses([]);
        setDisableButton(true);
      }
    } else {
      setOpen(false);
      setDisableButton(true);
      setAddresses([]);
    }
  };

  const handleEircodeChange = value => {
    if (!value || value.length < 7) {
      setIsEirCodeValid(true);
      setDisableButton(true);
      setOpen(false);
    }
    if (!value || value.length === 0) {
      setAddresses([]);
      setDisableButton(true);
      setIsEirCodeValid(false);
    }
    if (value.length > 0) {
      setIsEirCodeValid(true);
    }
    setAddressList(value);
  };

  /**
   * @name handleSubmit - Handle Check Availability button
   */
  const handleSubmit = () => {
    props.prequalHandler(graphql, selectedAddress);
  };
  return props.PrequalContentAlign === 'Center' && md ? (
    <Grid
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: '2rem',
        pl: 1,
      }}
    >
      <Box sx={styles(theme).prequalInputContainerBox}>
        <Autocomplete
          freeSolo
          loadingText='Loading..'
          forcePopupIcon={false}
          autoComplete
          sx={styles(theme, props.fontColor).EircodeAutocompleteField}
          options={addresses}
          open={open}
          PopperComponent={CustomPopper}
          filterOptions={filterOptions}
          getOptionLabel={option => formatAddress(option)}
          onChange={(event, value, reason) => {
            if (!value) {
              setDisableButton(true);
              setOpen(false);
              return;
            }
            setOpen(false);
            if (reason === 'selectOption') {
              // to close dropdown when user selects a address from dropdown
              setDisableButton(false);
              setIsEirCodeValid(true);
              setSelectedAddress(createAddressObject(value, eirCode));
            } else {
              setDisableButton(true);
            } // to create object from selected address from dropdown
          }}
          renderInput={params => (
            <TextField
              {...params}
              inputRef={props.scrollToInput}
              sx={styles(theme).EircodeInputField}
              placeholder='Enter your Eircode'
              error={!isEirCodeValid}
              onChange={e => {
                handleEircodeChange(e.target.value);
              }}
              /* eslint-disable react/jsx-no-duplicate-props */
              // inputProps is required to read error message by screen reader
              inputProps={{
                ...params.inputProps,
                'aria-errormessage': 'eircode-error-text',
                'aria-invalid': !isEirCodeValid,
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <LocationOnIcon sx={{ color: theme.palette.primary.buttonColor }} />
                ),
                endAdornment: null,
              }}
            />
          )}
        />
        <Typography
          component='a'
          variant='body2'
          color={props.fontColor}
          sx={styles(theme).findEircode}
          onClick={() => {
            window.open(`${publicRuntimeConfig.NEXT_PUBLIC_FIND_EIRCODE}`, '_blank');
          }}
          aria-label='Don’t know your eircode?'
        >
          Don’t know your eircode?
        </Typography>
        <Box aria-live='assertive'>
          {!isEirCodeValid && (
            <Typography
              id='eircode-error-text'
              component='p'
              variant='body1'
              color={props.fontColor}
              sx={styles(theme).errorText}
              role='alert'
            >
              Please, enter a valid Eircode
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ paddingLeft: '16px' }}>
        <Button
          id='check_via_eircode'
          variant='buttonMedium'
          disabled={disableButton}
          sx={{ ...styles(theme).checkAvailabilityButton, marginTop: '-24px' }}
          onClick={() => {
            handleSubmit();
          }}
          aria-label='Check availability'
        >
          Check availability
        </Button>
      </Box>
    </Grid>
  ) : (
    <Grid container sx={styles(theme).prequalInputContainer}>
      <Grid item xs={12} md={9} lg={6}>
        <Box sx={styles(theme).prequalInputContainerBox}>
          <Autocomplete
            freeSolo
            loadingText='Loading..'
            forcePopupIcon={false}
            autoComplete
            sx={styles(theme, props.fontColor).EircodeAutocompleteField}
            options={addresses}
            open={open}
            PopperComponent={CustomPopper}
            filterOptions={filterOptions}
            getOptionLabel={option => (typeof option === 'string' ? option : formatAddress(option))}
            onChange={(event, value, reason) => {
              if (!value) {
                setDisableButton(true);
                setOpen(false);
                return;
              }
              setOpen(false);
              if (reason === 'selectOption') {
                // to close dropdown when user selects a address from dropdown
                setDisableButton(false);
                setIsEirCodeValid(true);
                setSelectedAddress(createAddressObject(value, eirCode));
              } else {
                setDisableButton(true);
              } // to create object from selected address from dropdown
            }}
            renderInput={params => (
              <TextField
                {...params}
                inputRef={props.scrollToInput}
                sx={styles(theme).EircodeInputField}
                placeholder='Enter your Eircode'
                error={!isEirCodeValid}
                onChange={e => {
                  handleEircodeChange(e.target.value);
                }}
                /* eslint-disable react/jsx-no-duplicate-props */
                // inputProps is required to read error message by screen reader
                inputProps={{
                  ...params.inputProps,
                  'aria-errormessage': 'eircode-error-text',
                  'aria-invalid': !isEirCodeValid,
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <LocationOnIcon sx={{ color: theme.palette.primary.buttonColor }} />
                  ),
                  endAdornment: null,
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && disableButton) {
                    e.preventDefault();
                  }
                }}
              />
            )}
          />
          <Typography
            component='a'
            variant='body2'
            color={props.fontColor}
            sx={styles(theme).findEircode}
            onClick={() => {
              window.open(`${publicRuntimeConfig.NEXT_PUBLIC_FIND_EIRCODE}`, '_blank');
            }}
            aria-label='Don’t know your eircode?'
          >
            Don’t know your eircode?
          </Typography>
          <Box aria-live='assertive'>
            {!isEirCodeValid && (
              <Typography
                id='eircode-error-text'
                component='p'
                variant='body1'
                color={props.fontColor}
                sx={styles(theme).errorText}
                role='alert'
              >
                Please, enter a valid Eircode
              </Typography>
            )}
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12} md={3} lg={6} sx={styles(theme).checkAvailabilityButtonContainer}>
        <Button
          id='check_via_eircode'
          variant='buttonMedium'
          disableRipple
          disabled={disableButton}
          sx={styles(theme).checkAvailabilityButton}
          onClick={() => {
            handleSubmit();
          }}
          aria-label='Check availability'
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleSubmit();
              if (window.location.pathname === '/shop/broadband') {
                const header = document.getElementById('eir-header');
                if (!header) return;
                const allChildren = header.querySelectorAll('*');
                allChildren.forEach(el => {
                  el.setAttribute('tabindex', '-1');
                  el.setAttribute('aria-hidden', 'true');
                });
              }
            }
          }}
        >
          Check availability
        </Button>
      </Grid>
    </Grid>
  );
};

export default BroadbandPrequalInput;
